import { useEffect, useState } from "react";

import type {
  Questionnaire,
  QuestionnaireItem,
  ValueSetExpansionContains,
} from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import type { UseFormReturnType } from "@mantine/form";

import {
  isClientIppVitalsQuestionnaire,
  isNewbornVitalsQuestionnaire,
} from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/utils/normalize-postpartum-vitals-questionnaire";
import {
  getTemperatureUnitSelectValue,
  getUnitSystemFromTemperatureUnitSelectValue,
  parseCodingFromNativeSelect,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import { FormValues } from "@/encounters/components/EncounterLabor/types/labor";
import { VitalsUnitsToggle } from "@/encounters/components/VitalSigns/components/VitalsUnitsToggle";
import {
  convertTemperatureValue,
  getVitalsUnitFields,
  VitalsUnitSystem,
} from "@/encounters/components/VitalSigns/utils/vitals-unit-system";
import { QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import QuestionnaireForm from "@/intake-forms/components/QuestionnaireForm";
import { getValueSetOptions } from "@/utils/medplum";

interface PostpartumVitalsQuestionnaireFormProps {
  form: UseFormReturnType<FormValues>;
  questionnaire: Questionnaire;
}

/**
 * Renders a postpartum vitals questionnaire with Imperial/Metric radios after Date/Time,
 * matching native vitals drawers. Temperature unit stays on the form (hidden) for FHIR save.
 */
export function PostpartumVitalsQuestionnaireForm({
  form,
  questionnaire,
}: PostpartumVitalsQuestionnaireFormProps) {
  const rootGroup = questionnaire.item?.[0];
  const items = rootGroup?.item ?? [];
  const dateTimeIndex = items.findIndex((item) => item.linkId?.endsWith("DateTime"));

  if (!rootGroup || dateTimeIndex < 0) {
    return <QuestionnaireForm form={form} group={rootGroup} questionnaire={questionnaire} />;
  }

  const dateTimeGroup: QuestionnaireItem = {
    ...rootGroup,
    item: items.slice(0, dateTimeIndex + 1),
  };
  const remainingGroup: QuestionnaireItem = { ...rootGroup, item: items.slice(dateTimeIndex + 1) };

  return (
    <>
      <QuestionnaireForm form={form} group={dateTimeGroup} questionnaire={questionnaire} />
      <PostpartumVitalsUnitsToggle form={form} questionnaire={questionnaire} />
      <QuestionnaireForm form={form} group={remainingGroup} questionnaire={questionnaire} />
    </>
  );
}

function PostpartumVitalsUnitsToggle({
  form,
  questionnaire,
}: PostpartumVitalsQuestionnaireFormProps) {
  const medplum = useMedplum();
  const [temperatureUnitOptions, setTemperatureUnitOptions] = useState<ValueSetExpansionContains[]>(
    [],
  );
  const paths = getPostpartumVitalsTemperaturePaths(questionnaire);
  const unitSelectValue = readTemperatureUnitSelectValue(
    getNestedFormValue(form.values, paths.unit),
  );
  const unitSystem = getUnitSystemFromTemperatureUnitSelectValue(unitSelectValue);
  const temperatureUnit = getVitalsUnitFields(unitSystem).temperatureUnit;

  useEffect(() => {
    let isCancelled = false;
    const loadValueSets = async () => {
      const temperatureUnits = await getValueSetOptions(
        medplum,
        QuiltedHealthValueSets.TemperatureUnits,
      );
      if (!isCancelled) {
        setTemperatureUnitOptions(temperatureUnits ?? []);
      }
    };
    void loadValueSets();
    return () => {
      isCancelled = true;
    };
  }, [medplum]);

  const handleUnitSystemChange = (nextUnitSystem: VitalsUnitSystem) => {
    if (nextUnitSystem === unitSystem) return;

    const rawTemperature = getNestedFormValue(form.values, paths.value);
    const temperatureNumber =
      typeof rawTemperature === "number"
        ? rawTemperature
        : typeof rawTemperature === "string" && rawTemperature !== ""
          ? Number(rawTemperature)
          : undefined;

    const convertedTemperature = convertTemperatureValue(
      temperatureNumber != null && Number.isFinite(temperatureNumber)
        ? temperatureNumber
        : undefined,
      temperatureUnit,
      nextUnitSystem,
    );

    form.setFieldValue(
      paths.unit,
      parseCodingFromNativeSelect(
        getTemperatureUnitSelectValue(nextUnitSystem, temperatureUnitOptions),
      ),
    );
    if (convertedTemperature !== undefined) {
      form.setFieldValue(paths.value, convertedTemperature);
    }
  };

  const unitsTestId = isNewbornVitalsQuestionnaire(questionnaire)
    ? "newborn-vitals-units"
    : "ipp-vitals-units";

  return (
    <VitalsUnitsToggle
      value={unitSystem}
      data-testid={unitsTestId}
      testIdPrefix={unitsTestId}
      onChange={handleUnitSystemChange}
    />
  );
}

function getPostpartumVitalsTemperaturePaths(questionnaire: Questionnaire): {
  unit: string;
  value: string;
} {
  if (isClientIppVitalsQuestionnaire(questionnaire)) {
    return {
      unit: "ippVitalsTemperature.ippVitalsTemperatureUnit",
      value: "ippVitalsTemperature.ippVitalsTemperatureValue",
    };
  }

  return {
    unit: "newbornVitalsTemperature.newbornVitalsTemperatureUnit",
    value: "newbornVitalsTemperature.newbornVitalsTemperatureValue",
  };
}

function getNestedFormValue(values: FormValues, path: string): unknown {
  return path.split(".").reduce<unknown>((current, key) => {
    if (!current || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[key];
  }, values);
}

function readTemperatureUnitSelectValue(value: unknown): string | undefined {
  if (typeof value === "string" && value) return value;
  if (value && typeof value === "object" && "code" in value) return JSON.stringify(value);
  return undefined;
}
